import { Page } from "../components/FG/Page";
import type { PreviewGatePayload } from "../lib/boot";
import "./previewGate.css";

/**
 * Preview-gate screen — shown when the proxy gate refuses this request. Renders
 * inside the Shopify theme chrome (the SPA shell is served theme-wrapped), so a
 * gated shopper gets the site header/footer instead of the old standalone HTML
 * page. Content mirrors the server's `renderPreviewGatePage`: a preview message,
 * a login CTA, and — in `allowlist_or_code` mode — an access-code form.
 *
 * The access-code form is a plain `<form method="post">` posting to the
 * storefront-relative `preview-access` endpoint (a full navigation, not a
 * fetch), so it works even though the SPA itself was refused to a gated user.
 */
export function PreviewGateScreen({ previewGate }: { previewGate: PreviewGatePayload }) {
  const { loginUrl, codeForm } = previewGate;
  return (
    <Page bg="var(--fg-app-bg)">
      <div className="fgpg-wrap">
        <div className="fgpg-card">
          <span className="fg-eyebrow">Preview</span>
          <h1 className="fg-h3 fgpg-title">Fueling Plans is in preview</h1>
          <p className="fg-body fgpg-body">
            This tool is currently only available to preview users and requires login.
          </p>
          <p className="fg-body fgpg-body">
            If you&rsquo;re part of the preview, log in to your Fuel Goods account and reopen
            Fueling Plans.
          </p>
          <a className="fg-btn fg-btn--primary fgpg-cta" href={loginUrl}>
            Log in
          </a>

          {codeForm ? (
            <form method="post" action={codeForm.action} className="fgpg-form">
              <input type="hidden" name="return_to" value={codeForm.returnTo} />
              <label className="fgpg-label" htmlFor="preview-code">
                Have a preview access code?
              </label>
              {codeForm.error ? (
                <p className="fgpg-error" role="alert">
                  That code didn&rsquo;t work. Check it and try again.
                </p>
              ) : null}
              <div className="fgpg-row">
                <input
                  id="preview-code"
                  name="code"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  required
                  placeholder="Access code"
                  className="fgpg-input"
                />
                <button type="submit" className="fg-btn fg-btn--dark">
                  Unlock
                </button>
              </div>
            </form>
          ) : null}

          <p className="fgpg-fine">Not in the preview yet? Fueling Plans is coming to everyone soon.</p>
        </div>
      </div>
    </Page>
  );
}
